/**
 * UI platform filter value (Match History dropdown) -> API `platform` query param.
 * Stored value for Gamers Club is `gamers_club` (not a display string).
 */
export function platformFilterToApi(selectedValue) {
  if (!selectedValue) return '';
  return selectedValue.toLowerCase();
}

/** Button label for platform filter (Valve / Faceit / gamers_club slug, etc.) */
export function platformFilterDisplayLabel(selectedValue) {
  if (selectedValue === 'gamers_club') return 'Gamers Club';
  return selectedValue;
}

/**
 * Whether a match row's `platform` matches the selected filter (analyzing + merged lists).
 */
export function platformFilterMatches(selectedValue, matchPlatform) {
  const want = platformFilterToApi(selectedValue);
  if (!want) return true;
  return (matchPlatform || '').toLowerCase() === want;
}

/**
 * Copy for "comparison to … rank's average" (overview / statistics tooltips).
 */
export function rankComparisonPlatformLabel(platform, matchType) {
  if (platform === 'faceit') return 'Faceit';
  if (platform === 'gamers_club') return 'Gamers Club';
  if (platform === 'renown') return 'Renown';
  return matchType || '';
}

/**
 * A match payload's platform: /coach/matches/:id carries `match_platform`,
 * legacy payloads `platform`.
 */
export function matchPlatformOf(match) {
  return match?.match_platform ?? match?.platform ?? null;
}

// Keyed by the API's `platform` / `match_platform` value
export const PLATFORMS = {
  valve: { label: 'Valve', icon: '/img/icons/valve_icon.png' },
  faceit: { label: 'FACEIT', icon: '/img/icons/faceit_icon.png' },
  renown: { label: 'Renown', icon: '/img/icons/renown_icon.png' },
  gamers_club: { label: 'Gamers Club', icon: '/img/icons/gamers_club_icon.png' },
  refrag: { label: 'Refrag', icon: '/img/icons/refrag_icon.png' },
  pro: { label: 'HLTV', icon: null },
};

export const platformKey = (platform) => (platform || '').toLowerCase();
export const platformLabel = (platform) => PLATFORMS[platformKey(platform)]?.label || '';
export const platformIcon = (platform) => PLATFORMS[platformKey(platform)]?.icon || null;

// Refrag's match types are the matchmaking and LAN play modes. Uploaded demos and scrims are Refrag
// matches too, but they have their own tab, so the Refrag filter never offers them.
export const REFRAG_MODES = ['Wingman', 'Duels', 'Retakes'];

// CS2 competitive and wingman ranks run 0 to 18. The match history's type comes from Postgres,
// which cannot tell Premier from competitive, so a Valve match rated above that is Premier.
const SKILL_GROUP_MAX = 18;
export function isPremier(match) {
  const type = (match?.match_type || '').toLowerCase();
  if (type === 'premier') return true;
  return platformKey(matchPlatformOf(match)) === 'valve' && type === 'competitive'
    && [match?.end_rank, match?.start_rank].some((r) => r > SKILL_GROUP_MAX);
}

export function matchTypeLabel(match) {
  if (isPremier(match)) return 'Premier';
  const type = match?.match_type || '';
  return type ? type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ') : '';
}

// Match filters: API platform key => filter value, and each filter value's match types
export const FILTER_PLATFORMS = {
  valve: 'Valve', faceit: 'Faceit', renown: 'Renown', gamers_club: 'gamers_club', refrag: 'Refrag',
};
export const FILTER_MATCH_TYPES = {
  Valve: ['Competitive', 'Premier', 'Wingman'],
  Faceit: ['Matchmaking', 'Championship'],
  Renown: ['Matchmaking'],
  gamers_club: ['Competitive'],
  Refrag: REFRAG_MODES,
};
